import { TextInput } from "@ameelio/ui";
import { useMutation } from "@apollo/client/react";
import { useForm } from "react-hook-form";
import { Facility } from "../api/graphql";
import FormDialog from "../lib/FormDialog";
import useApolloErrorHandler from "../lib/handleApolloError";
import { isRequired } from "../lib/validate";
import { UpdateFacilityCodeDocument } from "./UpdateFacilityCode.generated";

export const MAX_FACILITY_CODE_LENGTH = 20;

type Props = {
  facility: Pick<Facility, "id" | "publicId">;
  onClose: () => void;
};

type FormData = {
  publicId: string;
};

export default function UpdateFacilityCodeDialog({ facility, onClose }: Props) {
  const {
    control,
    handleSubmit,
    formState: { isSubmitting, isDirty },
  } = useForm<FormData>({
    mode: "onTouched",
    defaultValues: { publicId: facility.publicId },
  });

  const handleApolloError = useApolloErrorHandler();
  const [updateCode] = useMutation(UpdateFacilityCodeDocument, {
    onError: (e) => handleApolloError(e),
  });

  const onSubmit = async ({ publicId }: FormData) => {
    await updateCode({
      variables: { input: { facilityId: facility.id, publicId } },
      onCompleted: onClose,
    });
  };

  return (
    <FormDialog
      title="Update code"
      onClose={onClose}
      onSubmit={handleSubmit(onSubmit)}
      submitting={isSubmitting}
      submitDisabled={!isDirty}
    >
      <TextInput
        control={control}
        name="publicId"
        label="Code"
        helperText={`Up to ${MAX_FACILITY_CODE_LENGTH} characters. Must match the facility value in the system's roster`}
        rules={isRequired("Please provide a valid code.")}
        slotProps={{ htmlInput: { maxLength: MAX_FACILITY_CODE_LENGTH } }}
      />
    </FormDialog>
  );
}
